// @flow

import type { AnyAction } from 'react-redux';
import { createReducer } from 'redux-act';

import type { Currency, Grid } from '@gandi/billing';

import {
  createSequoiaResource,
  type SequoiaResource,
  type SequoiaResourceType,
} from '@gandi/resource/redux';

import type { FullState } from '../../types';

export type Price = {
  currency: Currency,
  duration_unit: 'day' | 'year' | 'month',
  min_duration: number,
  max_duration: number,
  grid: Grid,
  price_after_taxes: number,
  price_before_taxes: number,
  process: 'add_wildcard' | 'add_fqdn',
  product: {
    name: 'sectigo_dv' | 'sectigo_ov',
    params: { provider: 'sectigo' | 'digicert', validation_type: 'dv' | 'ov' },
  },
  quantity: number,
  tax_rate: number,
  taxes: [
    {
      label: string,
      name: string,
      rate: number,
      type: string,
    },
  ],
};

const initialState: { [key: string]: Array<Price> } = {};

export type State = {
  quote: {
    [label: string]: Array<Price>,
  },
};

export type AcmeCatalogResourceType = SequoiaResourceType<{
  postPayload: {
    domains: Array<string>,
    provider?: ?string,
    validation_type?: ?string,
    grid?: ?string,
    currency?: ?string,
    process?: ?string,
    duration?: ?string,
    country?: ?string,
  },
  postResponse: Array<Price>,
  fetchResource: Price,
}>;

export type AcmeCatalogResource = SequoiaResource<AcmeCatalogResourceType>;

const acmeBaseCatalogResource: AcmeCatalogResource = createSequoiaResource(
  'acme',
  'quote',
  {
    postPath: '/acme/quote',
  },
);

const EMPTY_ARRAY: Array<Price> = [];

const selectAcmeCatalogResponse = (
  state: FullState,
  label: string,
): Array<Price> => {
  return state.acme.quote[label] ?? EMPTY_ARRAY;
};

// Reducer
const handler = (
  state: { [key: string]: Array<Price> },
  {
    response,
    label,
  }: { response: AcmeCatalogResourceType['fetchResponse'], label: string },
): { [key: string]: Array<Price> } => {
  const responseData =
    acmeBaseCatalogResource.options.getFetchResponseData(response);
  return {
    [label]: responseData,
  };
};

const resetHandler = () => ({});

export const reducer = (createReducer<Array<Price>, AnyAction>(
  {
    [acmeBaseCatalogResource.post.success.getType()]: handler,
    [acmeBaseCatalogResource.post.start.getType()]: resetHandler,
  },
  initialState,
): any);

export const acmeCatalogResource = {
  ...acmeBaseCatalogResource,
  selectAcmeCatalogResponse,
};
